import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  MessageCircle, ArrowLeft, Plus, Send, Paperclip, X,
  CircleDashed, CheckCircle2, Lock, Loader2, ImageIcon
} from 'lucide-react';
import { supportApi, contentApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

/**
 * In-app Contact Support modal — handles both list view & thread view.
 * Async, lightweight; never blocks editor (no global state, no canvas access).
 *
 * Props: { open, onOpenChange }
 */
export default function ContactSupportModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'new' | 'thread'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportApi.list();
      setTickets(res.data?.tickets || []);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load list whenever modal opens & view returns to list
  useEffect(() => {
    if (open && view === 'list') refreshList();
  }, [open, view, refreshList]);

  const openThread = async (ticket) => {
    setActiveTicket(ticket);
    setView('thread');
    setLoading(true);
    try {
      const res = await supportApi.get(ticket.id);
      setMessages(res.data?.messages || []);
      setActiveTicket(res.data?.ticket || ticket);
    } catch (e) {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      // Reset on dialog close
      setView('list');
      setActiveTicket(null);
      setMessages([]);
      setReplyText('');
      setAttachment(null);
    }
    onOpenChange(next);
  };

  const handleAttach = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`Only images supported. You picked: ${file.type || file.name}`);
      return;
    }
    // HEIC/HEIF (iPhone default) can be uploaded but most browsers can't render.
    // Reject up-front with a helpful message rather than letting users wonder.
    const fname = (file.name || '').toLowerCase();
    if (fname.endsWith('.heic') || fname.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      toast.error('iPhone HEIC images can\'t be displayed in browsers. Save as JPG/PNG and re-attach.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 4 MB)`);
      return;
    }
    try {
      setSubmitting(true);
      toast.loading('Uploading attachment…', { id: 'support-attach' });
      const res = await contentApi.upload(file);
      const id = res.data?.id;
      if (!id) throw new Error('Upload returned no id');
      const url = `/api/content/serve/${id}`;
      setAttachment({ url, name: file.name, previewUrl: URL.createObjectURL(file) });
      toast.success('Attached', { id: 'support-attach' });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Attachment failed: ${detail}`, { id: 'support-attach' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[85vh] overflow-hidden p-0 flex flex-col"
        data-testid="contact-support-modal"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-800 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3 min-w-0">
            {view !== 'list' && (
              <button
                onClick={() => setView('list')}
                className="text-zinc-400 hover:text-white p-1 -ml-1 rounded transition-colors"
                data-testid="support-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <DialogTitle className="text-zinc-100 flex items-center gap-2 text-base truncate">
              <MessageCircle className="w-4 h-4 text-cyan-400 shrink-0" />
              {view === 'list' && 'Contact Support'}
              {view === 'new' && 'New Request'}
              {view === 'thread' && (
                <span className="truncate">{activeTicket?.subject || 'Conversation'}</span>
              )}
            </DialogTitle>
          </div>
          {view === 'list' && (
            <Button
              size="sm"
              onClick={() => { setView('new'); setReplyText(''); setAttachment(null); }}
              className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs"
              data-testid="support-new-ticket-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New
            </Button>
          )}
        </DialogHeader>

        {/* ───────────── BODY ───────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {view === 'list' && (
            <TicketList tickets={tickets} loading={loading} onOpen={openThread} />
          )}
          {view === 'new' && (
            <NewTicketForm
              onCreated={(t) => { setView('list'); refreshList(); openThread(t); }}
              onCancel={() => setView('list')}
            />
          )}
          {view === 'thread' && activeTicket && (
            <ThreadView
              ticket={activeTicket}
              messages={messages}
              loading={loading}
              currentUserId={user?.id}
            />
          )}
        </div>

        {/* ───────────── REPLY BAR (thread only) ───────────── */}
        {view === 'thread' && activeTicket && activeTicket.status !== 'closed' && (
          <div className="border-t border-zinc-800 px-5 py-3 bg-zinc-900/80">
            {attachment && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-zinc-800 text-xs">
                <ImageIcon className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-zinc-300 truncate flex-1">{attachment.name}</span>
                <button
                  onClick={() => setAttachment(null)}
                  className="text-zinc-500 hover:text-zinc-200"
                  data-testid="support-remove-attachment"
                ><X className="w-3 h-3" /></button>
              </div>
            )}
            <div className="flex gap-2">
              <label
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-800 hover:bg-zinc-700 cursor-pointer text-zinc-300 hover:text-cyan-300 transition-colors shrink-0 border border-zinc-700"
                title="Attach screenshot"
              >
                <Paperclip className="w-4 h-4" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { handleAttach(e.target.files?.[0]); e.target.value = ''; }}
                  data-testid="support-reply-attach"
                />
              </label>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply…"
                rows={2}
                className="flex-1 resize-none bg-zinc-800 border-zinc-700 text-sm text-zinc-100 placeholder:text-zinc-500 min-h-[36px]"
                data-testid="support-reply-input"
              />
              <Button
                onClick={async () => {
                  if (!replyText.trim() || submitting) return;
                  setSubmitting(true);
                  try {
                    const res = await supportApi.reply(activeTicket.id, {
                      message: replyText.trim(),
                      attachment_url: attachment?.url || null,
                    });
                    setMessages((prev) => [...prev, res.data?.message]);
                    setReplyText('');
                    setAttachment(null);
                    if (res.data?.status) {
                      setActiveTicket((t) => t ? { ...t, status: res.data.status } : t);
                    }
                  } catch (e) {
                    toast.error('Failed to send reply');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={!replyText.trim() || submitting}
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 px-3 shrink-0"
                data-testid="support-reply-send-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
        {view === 'thread' && activeTicket?.status === 'closed' && (
          <div className="border-t border-zinc-800 px-5 py-3 text-xs text-zinc-500 flex items-center gap-2 bg-zinc-900/80">
            <Lock className="w-3.5 h-3.5" /> This ticket is closed.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════
// LIST VIEW
// ════════════════════════════════════════════════════════════════════
const TicketList = ({ tickets, loading, onOpen }) => {
  if (loading) {
    return (
      <div className="text-center py-10 text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading…
      </div>
    );
  }
  if (!tickets.length) {
    return (
      <div className="text-center py-12 px-4">
        <MessageCircle className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-300 text-sm font-medium">No support requests yet.</p>
        <p className="text-zinc-500 text-xs mt-1">
          Got a question? Click <span className="text-cyan-400">New</span> above to open one.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="support-ticket-list">
      {tickets.map((t) => (
        <button
          key={t.id}
          onClick={() => onOpen(t)}
          className="w-full text-left px-4 py-3 rounded-lg border border-zinc-800 bg-zinc-800/50 hover:bg-zinc-800 hover:border-cyan-500/40 transition-colors"
          data-testid={`support-ticket-${t.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-100 truncate">{t.subject}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2">
                <span>{new Date(t.last_message_at || t.created_at).toLocaleString()}</span>
                <span>·</span>
                <span>{t.message_count || 1} message{(t.message_count || 1) > 1 ? 's' : ''}</span>
              </div>
            </div>
            <StatusBadge status={t.status} hasUnread={(t.unread_for_user || 0) > 0} />
          </div>
        </button>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// NEW TICKET FORM
// ════════════════════════════════════════════════════════════════════
const NewTicketForm = ({ onCreated, onCancel }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleAttach = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`Only images supported. You picked: ${file.type || file.name}`);
      return;
    }
    const fname = (file.name || '').toLowerCase();
    if (fname.endsWith('.heic') || fname.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      toast.error('iPhone HEIC images can\'t be displayed in browsers. Save as JPG/PNG and re-attach.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 4 MB)`);
      return;
    }
    try {
      setSubmitting(true);
      toast.loading('Uploading attachment…', { id: 'support-new-attach' });
      const res = await contentApi.upload(file);
      const id = res.data?.id;
      if (!id) throw new Error('Upload returned no id');
      setAttachment({ url: `/api/content/serve/${id}`, name: file.name, previewUrl: URL.createObjectURL(file) });
      toast.success('Attached', { id: 'support-new-attach' });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Attachment failed: ${detail}`, { id: 'support-new-attach' });
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!subject.trim() || !message.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await supportApi.create({
        subject: subject.trim(),
        message: message.trim(),
        attachment_url: attachment?.url || null,
      });
      const t = res.data?.ticket;
      if (t) {
        toast.success('Request submitted');
        onCreated(t);
      }
    } catch (e) {
      toast.error('Failed to submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-zinc-400">Subject</Label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Briefly describe your issue"
          maxLength={200}
          className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-sm"
          data-testid="support-subject-input"
          autoFocus
        />
      </div>
      <div>
        <Label className="text-xs text-zinc-400">Description</Label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us what's happening, what you expected, and any steps to reproduce…"
          rows={6}
          className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-sm resize-none"
          data-testid="support-message-input"
        />
      </div>

      {attachment ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-cyan-500/40 text-xs">
          {attachment.previewUrl ? (
            <img src={attachment.previewUrl} alt="" className="w-8 h-8 rounded object-cover bg-black" />
          ) : (
            <ImageIcon className="w-4 h-4 text-cyan-400 shrink-0" />
          )}
          <span className="text-zinc-200 truncate flex-1">{attachment.name}</span>
          <button
            onClick={() => setAttachment(null)}
            className="text-zinc-500 hover:text-zinc-200"
          ><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-zinc-700 hover:border-cyan-500/60 hover:bg-zinc-800/40 cursor-pointer text-sm text-zinc-300 hover:text-zinc-100 transition-colors">
          <Paperclip className="w-4 h-4" />
          {submitting ? 'Uploading…' : 'Attach screenshot (optional, max 4 MB)'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { handleAttach(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="support-new-attach"
          />
        </label>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="support-new-cancel">
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={!subject.trim() || !message.trim() || submitting}
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="support-new-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : 'Submit Request'}
        </Button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// THREAD VIEW
// ════════════════════════════════════════════════════════════════════
const ThreadView = ({ ticket, messages, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-10 text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading…
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="support-thread">
      <div className="flex items-center justify-between text-[11px] text-zinc-500 pb-2 border-b border-zinc-800">
        <span>Opened {new Date(ticket.created_at).toLocaleString()}</span>
        <StatusBadge status={ticket.status} />
      </div>
      {messages.map((m) => (
        <MessageBubble key={m.id} m={m} />
      ))}
    </div>
  );
};

const MessageBubble = ({ m }) => {
  const isAdmin = m.sender_role === 'admin';
  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`} data-testid={`support-msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
        isAdmin
          ? 'bg-cyan-500/10 border border-cyan-500/30 text-zinc-100 rounded-bl-sm'
          : 'bg-fuchsia-500/10 border border-fuchsia-500/30 text-zinc-100 rounded-br-sm'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] uppercase tracking-wide font-semibold ${isAdmin ? 'text-cyan-300' : 'text-fuchsia-300'}`}>
            {isAdmin ? 'Support' : (m.sender_name || 'You')}
          </span>
          <span className="text-[10px] text-zinc-500">{new Date(m.created_at).toLocaleString()}</span>
        </div>
        <div className="whitespace-pre-wrap break-words leading-relaxed">{m.message}</div>
        {m.attachment_url && (
          <a
            href={m.attachment_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 block rounded-lg overflow-hidden border border-zinc-700 hover:border-cyan-400/60 transition-colors relative"
          >
            <img
              src={m.attachment_url}
              alt="Attachment"
              className="max-h-48 w-full object-contain bg-black"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const next = e.currentTarget.nextElementSibling;
                if (next) next.style.display = 'flex';
              }}
            />
            <div
              style={{ display: 'none' }}
              className="items-center gap-2 px-3 py-3 text-xs text-amber-300 bg-amber-950/30 border-t border-amber-700/40"
            >
              <ImageIcon className="w-4 h-4" />
              <span>Couldn't display attachment — open in new tab to view raw file.</span>
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// STATUS BADGE
// ════════════════════════════════════════════════════════════════════
const StatusBadge = ({ status, hasUnread }) => {
  const map = {
    open: { label: 'Pending', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300', icon: CircleDashed },
    replied: { label: 'Replied', cls: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300', icon: CheckCircle2 },
    closed: { label: 'Closed', cls: 'bg-zinc-700/40 border-zinc-700 text-zinc-400', icon: Lock },
  };
  const m = map[status] || map.open;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${m.cls}`}>
      <Icon className="w-3 h-3" />
      {m.label}
      {hasUnread && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />}
    </span>
  );
};
